import { getFirebaseBackend } from "@/helpers/authUtils";
import api from "@/helpers/api";
import { loadLanguageAsync } from "@/helpers/i18n";
export const state = {
  currentUser: JSON.parse(window.localStorage.getItem("auth.currentUser")),
  userProfile: JSON.parse(window.localStorage.getItem("auth.userProfile")),
  selectedCustomerAccountId: null,
};

const DEFAULT_USER_PREFERENCES = {
  language: "en-US",
  allow_image_upload: false,
  allow_alu_casing: false,
  allow_mixed_foams: false,
  allow_remills: false,
  enable_shipping_by_default: false,
  allow_duplicate_references: true,
  allow_glueing_enabled: false,
  allow_products: false,
};

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue;
    saveState("auth.currentUser", newValue);
  },
  SET_USER_PROFILE(state, newValue) {
    state.userProfile = newValue;
    saveState("auth.userProfile", newValue);
  },
  SET_LANGUAGE(state, newValue) {
    loadLanguageAsync(newValue);
  },
  SET_USER_PREFERENCES(state, newValue) {
    state.userProfile.preferences = { ...newValue };
    saveState("auth.userPofile", state.userProfile);
  },
  SET_SELECTED_CUSTOMER_ACCOUNT_ID(state, customerAccountId) {
    state.selectedCustomerAccountId = customerAccountId || null;
  },
};

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser;
  },
  userRole(state) {
    if (!state.userProfile) {
      return "noone";
    }
    return state.userProfile.role || "noone";
  },
  isManagement(state) {
    const role = state.userProfile?.role;
    return role === "admin";
  },
  requiresCustomerSelection(state) {
    if (!state.userProfile) {
      return true;
    }
    return !state.userProfile.customer_account_id;
  },
  needsCustomerStep(state) {
    return state.userProfile?.role === "admin";
  },
  userProfile(state) {
    return state.userProfile || {};
  },
  resolveCustomerAccountId: (state) => (data = {}) => {
    return (
      data.customer_account_id ||
      state.selectedCustomerAccountId ||
      state.userProfile?.customer_account_id ||
      null
    );
  },
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  // eslint-disable-next-line no-unused-vars
  init({ state, dispatch }) {
    dispatch("validate");
  },

  updateUserPassword(
    { commit, dispatch, state },
    { current_password, password }
  ) {
    const { email } = state.userProfile;
    return new Promise((resolve, reject) => {
      return getFirebaseBackend()
        .reauthenticateWithCredential(current_password)
        .then((res) => {
          return getFirebaseBackend()
            .updateUserPassword(password)
            .then((user) => {
              resolve(true);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  // Logs in the current user.
  logIn(
    { commit, dispatch, getters },
    { email, password, rememberMe = false } = {}
  ) {
    if (getters.loggedIn) return dispatch("validate");

    return getFirebaseBackend()
      .loginUser(email, password, rememberMe)
      .then((user) => {
        commit("SET_CURRENT_USER", { id: user.uid });
        dispatch("fetchUserProfile", { id: user.uid });
        return user;
      });
  },

  fetchUserProfile({ commit, dispatch }, payload) {
    const { id } = payload;
    return new Promise((resolve, reject) => {
      api.users
        .fetch({ id })
        .then((profile) => {
          if (profile === null) {
            dispatch("logOut");
            return reject(new Error("User profile not found"));
          }

          var prefs = {
            ...DEFAULT_USER_PREFERENCES,
            ...(profile.preferences || {}),
          };

          commit("SET_LANGUAGE", prefs.language);
          commit("SET_USER_PROFILE", profile);
          commit("SET_USER_PREFERENCES", prefs);
          if (profile.role !== "admin") {
            commit("SET_SELECTED_CUSTOMER_ACCOUNT_ID", null);
          }
          resolve(profile);
        })
        .catch((err) => reject(err));
    });
  },

  // Logs out the current user.
  logOut({ commit }) {
    // eslint-disable-next-line no-unused-vars
    commit("SET_CURRENT_USER", null);
    commit("SET_USER_PROFILE", null);
    commit("SET_SELECTED_CUSTOMER_ACCOUNT_ID", null);
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line no-unused-vars
      getFirebaseBackend()
        .logout()
        .then((response) => {
          resolve(true);
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  },

  // register the user
  register({ commit, dispatch, getters }, { email, password } = {}) {
    if (getters.loggedIn) return dispatch("validate");

    return getFirebaseBackend()
      .registerUser(email, password)
      .then((response) => {
        const user = response;
        commit("SET_CURRENT_USER", user);
        return user;
      });
  },

  // register the user
  // eslint-disable-next-line no-unused-vars
  resetPassword({ commit, dispatch, getters }, { email } = {}) {
    if (getters.loggedIn) return dispatch("validate");

    return getFirebaseBackend()
      .forgetPassword(email)
      .then((response) => {
        const message = response.data;
        return message;
      });
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  // eslint-disable-next-line no-unused-vars
  validate({ commit, state, dispatch }) {
    if (!state.currentUser) return Promise.resolve(null);
    const user = getFirebaseBackend().getAuthenticatedUser();
    if (user === null) dispatch("logOut");
    else {
      commit("SET_CURRENT_USER", { id: user.uid });
      dispatch("fetchUserProfile", { id: user.uid });
      return user;
    }
  },
};

// ===
// Private helpers
// ===

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}
